import { Component } from '@theme/component';
import { lockScroll, unlockScroll } from '@theme/utilities';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];
  #activeIndex = 0;
  #opener;
  #pointerStart;
  #controller;
  #zoomImage;
  #zoomBox;
  #zoomPoint = { x: 0.5, y: 0.5 };

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const { dialog } = this.refs;
    dialog.addEventListener('pointerdown', this.#handlePointerDown, { signal });
    dialog.addEventListener('pointermove', this.#moveZoom, { signal });
    dialog.addEventListener('dragstart', event => { if (event.target.closest('.about-image-popup__image')) event.preventDefault(); }, { signal });
    window.addEventListener('resize', () => this.#resetZoom(), { signal });
    dialog.addEventListener('pointerup', this.#handlePointerUp, { signal });
    dialog.addEventListener('pointercancel', this.#clearPointer, { signal });
    dialog.addEventListener('cancel', this.#handleCancel, { signal });
    dialog.addEventListener('close', this.#afterClose, { signal });
    dialog.addEventListener('keydown', this.#onKeyDown, { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    if (this.refs.dialog) unlockScroll(this.refs.dialog);
    super.disconnectedCallback();
  }

  open(index, event) {
    event?.preventDefault();
    const { dialog, media } = this.refs;
    if (dialog.open || !media.length) return;
    this.#opener = document.activeElement;
    this.#activeIndex = Math.max(0, Math.min(Number(index) || 0, media.length - 1));
    lockScroll(dialog);
    dialog.showModal();
    this.selectThumbnail(this.#activeIndex);
    dialog.querySelector('.about-image-popup__close')?.focus({ preventScroll: true });
  }

  loadHighResolutionImage(mediaContainer) {
    const image = mediaContainer?.querySelector('img.product-media__image');
    if (image instanceof HTMLImageElement) image.loading = 'eager';
  }

  selectThumbnail(index) {
    index = Number(index);
    const { media, thumbnails, dialog } = this.refs;
    if (!Number.isInteger(index) || index < 0 || index >= media.length) return;
    this.#resetZoom();
    this.#activeIndex = index;
    media.forEach((item, position) => {
      item.hidden = position !== index;
      if (item.hidden) item.querySelectorAll('video').forEach(video => video.pause());
    });
    thumbnails.querySelectorAll('button').forEach((button, position) => {
      button.setAttribute('aria-pressed', String(position === index));
    });
    const thumbnail = thumbnails.children[index];
    if (thumbnail instanceof HTMLElement) {
      const rail = thumbnails.getBoundingClientRect();
      const item = thumbnail.getBoundingClientRect();
      thumbnails.scrollBy({ left: item.left + item.width / 2 - rail.left - rail.width / 2, behavior: 'instant' });
    }
    const status = dialog.querySelector('[data-image-status]');
    if (status) status.textContent = `${index + 1} / ${media.length}`;
    const surface = media[index].querySelector('.about-image-popup__image');
    if (surface) {
      surface.tabIndex = 0;
      surface.setAttribute('role', 'button');
      surface.setAttribute('aria-label', 'Zoom image');
      surface.setAttribute('aria-pressed', 'false');
    }
    this.loadHighResolutionImage(media[index]);
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }

  handleThumbnailClick(index) {
    this.selectThumbnail(index);
  }

  handleThumbnailPointerEnter(index) {
    this.loadHighResolutionImage(this.refs.media[Number(index)]);
  }

  previous() {
    this.selectThumbnail((this.#activeIndex - 1 + this.refs.media.length) % this.refs.media.length);
  }

  next() {
    this.selectThumbnail((this.#activeIndex + 1) % this.refs.media.length);
  }

  close() {
    this.closeDialog();
  }

  closeDialog() {
    if (this.refs.dialog.open) this.refs.dialog.close();
  }

  #afterClose = () => {
    this.#resetZoom();
    unlockScroll(this.refs.dialog);
    this.refs.dialog.querySelectorAll('video').forEach(video => video.pause());
    if (this.#opener instanceof HTMLElement && this.#opener.isConnected) {
      this.#opener.focus({ preventScroll: true });
    }
    this.#pointerStart = undefined;
    window.dispatchEvent(new DialogCloseEvent());
  };

  #handleCancel = event => {
    event.preventDefault();
    this.close();
  };

  #onKeyDown = event => this.handleKeyDown(event);

  handleKeyDown(event) {
    if ((event.key === 'Enter' || event.key === ' ') && event.target.matches('.about-image-popup__image')) {
      event.preventDefault();
      this.#toggleZoom(event.target);
      return;
    }
    if (this.#zoomImage && ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
      event.preventDefault();
      this.#zoomPoint.x += event.key === 'ArrowRight' ? 0.08 : event.key === 'ArrowLeft' ? -0.08 : 0;
      this.#zoomPoint.y += event.key === 'ArrowDown' ? 0.08 : event.key === 'ArrowUp' ? -0.08 : 0;
      this.#applyZoom();
      return;
    }
    if (event.key === 'Tab') {
      const controls = Array.from(this.refs.dialog.querySelectorAll('button:not([disabled]), a[href], input, select, textarea, [tabindex="0"]'))
        .filter(element => element.getClientRects().length > 0 && !element.closest('[hidden]'));
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && event.target === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && event.target === last) {
        event.preventDefault();
        first?.focus();
      }
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.target.closest('video, iframe, model-viewer, input, select, textarea')) return;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      const rtl = getComputedStyle(this).direction === 'rtl';
      (event.key === 'ArrowRight') !== rtl ? this.next() : this.previous();
    }
  }

  #outside(event) {
    if (event.target !== this.refs.dialog) return false;
    const box = this.refs.dialog.getBoundingClientRect();
    return event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom;
  }

  #handlePointerDown = event => {
    if (!event.isPrimary) {
      this.#clearPointer();
      return;
    }
    this.#pointerStart = {
      id: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      outside: this.#outside(event),
      surface: event.target.closest('.about-image-popup__image'),
      zoomed: !!this.#zoomImage,
      swipe: !this.#zoomImage && event.pointerType === 'touch' && !!event.target.closest('.about-image-popup__image'),
    };
  };

  #handlePointerUp = event => {
    const start = this.#pointerStart;
    this.#clearPointer();
    if (!start || start.id !== event.pointerId) return;
    if (start.outside && this.#outside(event)) {
      this.close();
      return;
    }
    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    if (start.surface && Math.hypot(dx, dy) < 10 && !start.moved) {
      this.#toggleZoom(start.surface, event);
      return;
    }
    if (start.swipe && Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      const rtl = getComputedStyle(this).direction === 'rtl';
      (dx < 0) !== rtl ? this.next() : this.previous();
    }
  };

  #toggleZoom(surface, event) {
    if (this.#zoomImage) { this.#resetZoom(); return; }
    const image = surface.querySelector('img.product-media__image');
    if (!image?.naturalWidth) return;
    this.#zoomImage = image;
    this.#zoomBox = image.getBoundingClientRect();
    this.#zoomPoint = event ? {
      x: (event.clientX - this.#zoomBox.left) / this.#zoomBox.width,
      y: (event.clientY - this.#zoomBox.top) / this.#zoomBox.height
    } : { x: 0.5, y: 0.5 };
    surface.classList.add('is-magnified');
    surface.setAttribute('aria-pressed', 'true');
    surface.setAttribute('aria-label', 'Zoom out. Move across the image or use arrow keys to explore.');
    image.dataset.originalSizes = image.sizes;
    image.sizes = Math.ceil(this.#zoomBox.width * 2.5) + 'px';
    this.#applyZoom();
  }

  #applyZoom() {
    this.#zoomPoint.x = Math.max(0, Math.min(1, this.#zoomPoint.x));
    this.#zoomPoint.y = Math.max(0, Math.min(1, this.#zoomPoint.y));
    this.#zoomImage.style.transformOrigin = (this.#zoomPoint.x * 100) + '% ' + (this.#zoomPoint.y * 100) + '%';
    this.#zoomImage.style.transform = 'scale(2.5)';
  }

  #moveZoom = event => {
    const start = this.#pointerStart;
    if (start && Math.hypot(event.clientX - start.x, event.clientY - start.y) > 10) start.moved = true;
    if (!this.#zoomImage || !event.isPrimary) return;
    if (event.pointerType === 'touch' && !start?.zoomed) return;
    if (!event.target.closest('.about-image-popup__image')) return;
    this.#zoomPoint = {
      x: (event.clientX - this.#zoomBox.left) / this.#zoomBox.width,
      y: (event.clientY - this.#zoomBox.top) / this.#zoomBox.height
    };
    this.#applyZoom();
  };

  #resetZoom() {
    if (!this.#zoomImage) return;
    const surface = this.#zoomImage.closest('.about-image-popup__image');
    surface?.classList.remove('is-magnified');
    surface?.setAttribute('aria-pressed', 'false');
    surface?.setAttribute('aria-label', 'Zoom image');
    this.#zoomImage.style.removeProperty('transform');
    this.#zoomImage.style.removeProperty('transform-origin');
    this.#zoomImage.sizes = this.#zoomImage.dataset.originalSizes || '';
    delete this.#zoomImage.dataset.originalSizes;
    this.#zoomImage = undefined;
    this.#zoomBox = undefined;
  }

  #clearPointer = () => {
    this.#pointerStart = undefined;
  };
}

if (!customElements.get('zoom-dialog')) customElements.define('zoom-dialog', ZoomDialog);
